import React, { createContext, useContext, useEffect, useState } from 'react';
import Lenis from 'lenis';
import { loadGSAP } from '@/lib/gsap';

interface LenisContextType {
  lenis: Lenis | null;
}

const LenisContext = createContext<LenisContextType>({ lenis: null });

export const useLenis = () => {
  const context = useContext(LenisContext);
  if (!context) {
    throw new Error('useLenis must be used within LenisProvider');
  }
  return context;
};

export const LenisProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [lenis, setLenis] = useState<Lenis | null>(null);

  useEffect(() => {
    const lenisInstance = new Lenis({
      autoRaf: false,
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      touchMultiplier: 2,
      infinite: false,
      anchors: true,
      syncTouch: false,
    });

    setLenis(lenisInstance);

    // Start with native rAF so scroll works immediately
    let nativeRafId: number | null = null;
    const nativeRaf = (time: number) => {
      lenisInstance.raf(time);
      nativeRafId = requestAnimationFrame(nativeRaf);
    };
    nativeRafId = requestAnimationFrame(nativeRaf);

    // Load GSAP async, then switch to gsap.ticker
    let gsapTickerUpdate: ((time: number) => void) | null = null;
    let scrollTriggerUpdate: (() => void) | null = null;

    loadGSAP().then(({ gsap, ScrollTrigger }) => {
      // Stop native rAF
      if (nativeRafId !== null) {
        cancelAnimationFrame(nativeRafId);
        nativeRafId = null;
      }

      // Sync ScrollTrigger with Lenis
      scrollTriggerUpdate = ScrollTrigger.update;
      lenisInstance.on('scroll', scrollTriggerUpdate);

      // Use GSAP ticker as single RAF source
      gsapTickerUpdate = (time: number) => {
        lenisInstance.raf(time * 1000); // GSAP uses seconds, Lenis uses ms
      };

      gsap.ticker.add(gsapTickerUpdate);
      gsap.ticker.lagSmoothing(500, 33);
    });

    return () => {
      // Clean up native rAF if still active
      if (nativeRafId !== null) {
        cancelAnimationFrame(nativeRafId);
      }

      // Clean up GSAP ticker if it was set up
      if (gsapTickerUpdate || scrollTriggerUpdate) {
        loadGSAP().then(({ gsap }) => {
          if (gsapTickerUpdate) gsap.ticker.remove(gsapTickerUpdate);
        });
        if (scrollTriggerUpdate) {
          lenisInstance.off('scroll', scrollTriggerUpdate);
        }
      }

      lenisInstance.destroy();
      setLenis(null);
    };
  }, []);

  return (
    <LenisContext.Provider value={{ lenis }}>{children}</LenisContext.Provider>
  );
};
